import { supabaseDataProvider } from 'ra-supabase';

import {
    CreateParams,
    DataProvider,
    GetListParams,
    Identifier,
    UpdateParams,
    withLifecycleCallbacks,
} from 'react-admin';
import {
    Contact,
    ContactNote,
    Quote,
    QuoteNote,
    RAFile,
    Sale,
    SignUpData,
} from '../../types';
import { getActivityLog } from '../commons/activity';
import { getAccountAvatar } from '../commons/getAccountAvatar';
import { getContactAvatar } from '../commons/getContactAvatar';
import { getIsInitialized } from './authProvider';
import { supabase } from './supabase';

if (import.meta.env.VITE_SUPABASE_URL === undefined) {
    throw new Error('Please set the VITE_SUPABASE_URL environment variable');
}
if (import.meta.env.VITE_SUPABASE_ANON_KEY === undefined) {
    throw new Error(
        'Please set the VITE_SUPABASE_ANON_KEY environment variable'
    );
}

const baseDataProvider = supabaseDataProvider({
    instanceUrl: import.meta.env.VITE_SUPABASE_URL,
    apiKey: import.meta.env.VITE_SUPABASE_ANON_KEY,
    supabaseClient: supabase,
    sortOrder: 'asc,desc.nullslast' as any,
});

const processAccountLogo = async (params: any) => {
    let logo = params.data.logo;

    if (typeof logo !== 'object' || logo === null || !logo.src) {
        logo = await getAccountAvatar(params.data);
    } else if (logo.rawFile instanceof File) {
        await uploadToBucket(logo);
    }

    return {
        ...params,
        data: {
            ...params.data,
            logo,
        },
    };
};

async function processContactAvatar(
    params: UpdateParams<Contact>
): Promise<UpdateParams<Contact>>;

async function processContactAvatar(
    params: CreateParams<Contact>
): Promise<CreateParams<Contact>>;

async function processContactAvatar(
    params: CreateParams<Contact> | UpdateParams<Contact>
): Promise<CreateParams<Contact> | UpdateParams<Contact>> {
    const { data } = params;
    if (data.avatar?.src || !data.email_jsonb || !data.email_jsonb.length) {
        return params;
    }
    const avatarUrl = await getContactAvatar(data);

    // Clone the data and modify the clone
    const newData = { ...data, avatar: { src: avatarUrl || undefined } };

    return { ...params, data: newData };
}

const dataProviderWithCustomMethods = {
    ...baseDataProvider,
    async getList(resource: string, params: GetListParams) {
        if (resource === 'accounts') {
            return baseDataProvider.getList('accounts_summary', params);
        }
        if (resource === 'contacts') {
            return baseDataProvider.getList('contacts_summary', params);
        }

        return baseDataProvider.getList(resource, params);
    },
    async getOne(resource: string, params: any) {
        if (resource === 'accounts') {
            return baseDataProvider.getOne('accounts_summary', params);
        }
        if (resource === 'contacts') {
            return baseDataProvider.getOne('contacts_summary', params);
        }

        return baseDataProvider.getOne(resource, params);
    },

    async update(resource: string, params: UpdateParams<Sale>) {
        if (resource === 'sales') {
            const result = await (this as any).salesUpdate(
                params.id,
                params.data
            );
            return { data: result.data };
        }
        return baseDataProvider.update(resource, params);
    },

    async signUp({ email, password, first_name, last_name }: SignUpData) {
        const response = await supabase.auth.signUp({
            email,
            password,
            options: {
                data: {
                    first_name,
                    last_name,
                },
            },
        });

        if (!response.data?.user || response.error) {
            console.error('signUp.error', response.error);
            throw new Error('Failed to create account');
        }

        // Update the is initialized cache
        getIsInitialized._is_initialized_cache = true;

        return {
            id: response.data.user.id,
            email,
            password,
        };
    },
    async salesCreate(body: Partial<Sale>) {
        let processedBody = { ...body };

        // Handle Avatar Upload
        if (
            processedBody.avatar &&
            typeof processedBody.avatar === 'object' &&
            (processedBody.avatar as any).rawFile
        ) {
            try {
                const avatarFile = processedBody.avatar as RAFile; // Assume RAFile structure
                if (
                    avatarFile.src?.startsWith('blob:') ||
                    avatarFile.src?.startsWith('data:')
                ) {
                    const uploadedAvatar = await uploadToBucket(avatarFile);
                    // Check upload result before assigning
                    processedBody.avatar = uploadedAvatar?.src
                        ? uploadedAvatar.src
                        : undefined;
                } else if (avatarFile.src) {
                    // If src is already a string (URL), keep it
                    processedBody.avatar = avatarFile.src;
                } else {
                    delete processedBody.avatar; // Remove if invalid structure without src
                }
            } catch (uploadError) {
                console.error('Avatar upload failed:', uploadError);
                throw new Error('Failed to upload avatar.');
            }
        }
        // Handle Logo Upload (similar logic)
        if (
            processedBody.company_logo &&
            typeof processedBody.company_logo === 'object' &&
            (processedBody.company_logo as any).rawFile
        ) {
            try {
                const logoFile = processedBody.company_logo as RAFile;
                if (
                    logoFile.src?.startsWith('blob:') ||
                    logoFile.src?.startsWith('data:')
                ) {
                    const uploadedLogo = await uploadToBucket(logoFile);
                    // Check upload result before assigning
                    processedBody.company_logo = uploadedLogo?.src
                        ? uploadedLogo.src
                        : undefined;
                } else if (logoFile.src) {
                    processedBody.company_logo = logoFile.src;
                } else {
                    delete processedBody.company_logo;
                }
            } catch (uploadError) {
                console.error('Company logo upload failed:', uploadError);
                throw new Error('Failed to upload company logo.');
            }
        }

        const { data, error } = await supabase.functions.invoke<Sale>('users', {
            method: 'POST',
            body: processedBody, // Pass the processed body with URL strings
        });

        if (!data || error) {
            console.error('salesCreate.error', error);
            throw new Error('Failed to create account manager');
        }

        return data;
    },
    async salesUpdate(id: Identifier, data: Partial<Sale>) {
        let processedData = { ...data };

        // Handle Avatar Upload (similar to create)
        if (
            processedData.avatar &&
            typeof processedData.avatar === 'object' &&
            (processedData.avatar as any).rawFile
        ) {
            try {
                const avatarFile = processedData.avatar as RAFile;
                if (
                    avatarFile.src?.startsWith('blob:') ||
                    avatarFile.src?.startsWith('data:')
                ) {
                    const uploadedAvatar = await uploadToBucket(avatarFile);
                    // Check upload result before assigning
                    processedData.avatar = uploadedAvatar?.src
                        ? uploadedAvatar.src
                        : undefined;
                } else if (avatarFile.src) {
                    processedData.avatar = avatarFile.src;
                } else {
                    delete processedData.avatar;
                }
            } catch (uploadError) {
                console.error(
                    'Avatar upload failed during update:',
                    uploadError
                );
                throw new Error('Failed to upload avatar during update.');
            }
        } else if (
            processedData.avatar &&
            typeof processedData.avatar !== 'string'
        ) {
            // If it's not a string and not a rawFile object, remove it to avoid errors
            delete processedData.avatar;
        }

        // Handle Logo Upload (similar to create)
        if (
            processedData.company_logo &&
            typeof processedData.company_logo === 'object' &&
            (processedData.company_logo as any).rawFile
        ) {
            try {
                const logoFile = processedData.company_logo as RAFile;
                if (
                    logoFile.src?.startsWith('blob:') ||
                    logoFile.src?.startsWith('data:')
                ) {
                    const uploadedLogo = await uploadToBucket(logoFile);
                    // Check upload result before assigning
                    processedData.company_logo = uploadedLogo?.src
                        ? uploadedLogo.src
                        : undefined;
                } else if (logoFile.src) {
                    processedData.company_logo = logoFile.src;
                } else {
                    delete processedData.company_logo;
                }
            } catch (uploadError) {
                console.error(
                    'Company logo upload failed during update:',
                    uploadError
                );
                throw new Error('Failed to upload company logo during update.');
            }
        } else if (
            processedData.company_logo &&
            typeof processedData.company_logo !== 'string'
        ) {
            delete processedData.company_logo;
        }

        // Destructure ALL fields including the new preferences
        const {
            email,
            first_name,
            last_name,
            administrator,
            avatar,
            disabled,
            approved,
            company_name,
            agent_number,
            street,
            city,
            state,
            zip,
            company_logo,
            licensed_states,
            appointed_carriers,
        } = processedData;

        const bodyPayload = {
            sales_id: id,
            email,
            first_name,
            last_name,
            administrator,
            disabled,
            approved,
            avatar,
            company_name,
            agent_number,
            street,
            city,
            state,
            zip,
            company_logo,
            licensed_states,
            appointed_carriers, // Add the new fields
        };

        const { data: sale, error } = await supabase.functions.invoke<Sale>(
            'users',
            {
                method: 'PATCH',
                body: bodyPayload,
            }
        );

        if (error) {
            console.error('salesUpdate.error', error);
            throw new Error('Failed to update sales');
        }

        return { data: sale };
    },
    async updatePassword(id: Identifier) {
        const { data: passwordUpdated, error } =
            await supabase.functions.invoke<boolean>('updatePassword', {
                method: 'PATCH',
                body: {
                    sales_id: id,
                },
            });

        if (!passwordUpdated || error) {
            console.error('passwordUpdate.error', error);
            throw new Error('Failed to update password');
        }

        return passwordUpdated;
    },
    async unarchiveQuote(quote: Quote) {
        // get all quotes where stage is the same as the quote to unarchive
        const { data: quotes } = await baseDataProvider.getList<Quote>(
            'quotes',
            {
                filter: { stage: quote.stage },
                pagination: { page: 1, perPage: 1000 },
                sort: { field: 'index', order: 'ASC' },
            }
        );

        // set index for each quote starting from 1, if the quote to unarchive is found, set its index to the last one
        const updatedQuotes = quotes.map((d, index) => ({
            ...d,
            index: d.id === quote.id ? 0 : index + 1,
            archived_at: d.id === quote.id ? null : d.archived_at,
        }));

        return await Promise.all(
            updatedQuotes.map(updatedQuote =>
                baseDataProvider.update('quotes', {
                    id: updatedQuote.id,
                    data: updatedQuote,
                    previousData: quotes.find(d => d.id === updatedQuote.id),
                })
            )
        );
    },
    async getActivityLog(accountId?: Identifier) {
        return getActivityLog(baseDataProvider, accountId);
    },
    async isInitialized() {
        return getIsInitialized();
    },
} satisfies DataProvider;

export type CrmDataProvider = typeof dataProviderWithCustomMethods;

export const dataProvider = withLifecycleCallbacks(
    dataProviderWithCustomMethods,
    [
        {
            resource: 'contactNotes',
            beforeSave: async (data: ContactNote, _, __) => {
                if (data.attachments) {
                    for (const fi of data.attachments) {
                        await uploadToBucket(fi);
                    }
                }
                return data;
            },
        },
        {
            resource: 'quoteNotes',
            beforeSave: async (data: QuoteNote, _, __) => {
                if (data.attachments) {
                    for (const fi of data.attachments) {
                        await uploadToBucket(fi);
                    }
                }
                return data;
            },
        },
        {
            resource: 'contacts',
            beforeCreate: async params => {
                return processContactAvatar(params);
            },
            beforeUpdate: async params => {
                return processContactAvatar(params);
            },
            beforeGetList: async params => {
                return applyFullTextSearch([
                    'first_name',
                    'last_name',
                    'account_name',
                    'title',
                    'email',
                    'phone',
                    'background',
                ])(params);
            },
        },
        {
            resource: 'accounts',
            beforeGetList: async params => {
                return applyFullTextSearch([
                    'name',
                    'phone_number',
                    'website',
                    'zipcode',
                    'city',
                    'stateAbbr',
                ])(params);
            },
            beforeCreate: async params => {
                const createParams = await processAccountLogo(params);

                return {
                    ...createParams,
                    data: {
                        ...createParams.data,
                        created_at: new Date().toISOString(),
                    },
                };
            },
            beforeUpdate: async params => {
                return await processAccountLogo(params);
            },
        },
        {
            resource: 'contacts_summary',
            beforeGetList: async params => {
                return applyFullTextSearch(['first_name', 'last_name'])(params);
            },
        },
        {
            resource: 'quotes',
            beforeGetList: async params => {
                const processed = applyFullTextSearch([
                    'name',
                    'description',
                    'state',
                    'county',
                    'township',
                    'range',
                    'crop',
                ])(params);

                const {
                    county,
                    township,
                    range,
                    crop,
                    carrier,
                    hailProduct,
                    ...restFilter
                } = processed.filter;
                const newFilter = { ...restFilter };

                if (county) newFilter['county@ilike'] = `%${county}%`;
                if (township) newFilter['township@ilike'] = `%${township}%`;
                if (range) newFilter['range@ilike'] = `%${range}%`;
                if (crop) newFilter['crop@ilike'] = `%${crop}%`;
                if (carrier)
                    newFilter['selectedCarriers@cs'] = `["${carrier}"]`;
                if (hailProduct)
                    newFilter['selectedHailProducts@cs'] = `["${hailProduct}"]`;

                return { ...processed, filter: newFilter };
            },
        },
    ]
);

const applyFullTextSearch = (columns: string[]) => (params: GetListParams) => {
    if (!params.filter?.q) {
        return params;
    }
    const { q, ...filter } = params.filter;
    return {
        ...params,
        filter: {
            ...filter,
            '@or': columns.reduce((acc, column) => {
                if (column === 'email')
                    return {
                        ...acc,
                        [`email_fts@ilike`]: q,
                    };
                if (column === 'phone')
                    return {
                        ...acc,
                        [`phone_fts@ilike`]: q,
                    };
                else
                    return {
                        ...acc,
                        [`${column}@ilike`]: q,
                    };
            }, {}),
        },
    };
};

const uploadToBucket = async (fi: RAFile) => {
    if (!fi.src.startsWith('blob:') && !fi.src.startsWith('data:')) {
        // Sign URL check if path exists in the bucket
        if (fi.path) {
            const { error } = await supabase.storage
                .from('attachments')
                .createSignedUrl(fi.path, 60);

            if (!error) {
                return;
            }
        }
    }

    const dataContent = fi.src
        ? await fetch(fi.src).then(res => res.blob())
        : fi.rawFile;

    const file = fi.rawFile;
    const fileExt = file.name.split('.').pop();
    const fileName = `${Math.random()}.${fileExt}`;
    const filePath = `${fileName}`;
    const { error: uploadError } = await supabase.storage
        .from('attachments')
        .upload(filePath, dataContent);

    if (uploadError) {
        console.error('uploadError', uploadError);
        throw new Error('Failed to upload attachment');
    }

    const { data } = supabase.storage
        .from('attachments')
        .getPublicUrl(filePath);

    fi.path = filePath;
    fi.src = data.publicUrl;

    // save MIME type
    const mimeType = file.type;
    fi.type = mimeType;

    return fi;
};
